import React, { useEffect, useState } from 'react';
import { useInView } from '../utils/useInView';

const DURATION_MS = 900;

type Props = {
  /** Final numeric value to count up to. */
  value: number;
  /** Optional prefix rendered before the number, e.g. `×`. */
  prefix?: string;
  /** Optional suffix rendered after the number. */
  suffix?: string;
  className?: string;
};

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// requestAnimationFrame-driven ticker that animates from 0 → value the
// first time the element scrolls into view. Reduced-motion users see
// the final value immediately. No external animation library.
const CountUp: React.FC<Props> = ({
  value,
  prefix = '',
  suffix = '',
  className = '',
}) => {
  const { ref, inView } = useInView<HTMLSpanElement>();
  // Skip the animation entirely for reduced-motion users or non-positive
  // values — render the final value from first paint.
  const skipAnim = prefersReducedMotion() || value <= 0;
  const [display, setDisplay] = useState(() => (skipAnim ? value : 0));

  useEffect(() => {
    if (!inView || skipAnim) return;
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / DURATION_MS);
      // easeOutCubic — fast start, soft landing.
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(Math.round(value * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value, skipAnim]);

  return (
    <span ref={ref} className={className}>
      {prefix}
      {display}
      {suffix}
    </span>
  );
};

export default CountUp;
